import { useSyncExternalStore } from 'react';
import { SPLASH_FADE_MS } from './splashTokens';
import { ldLog } from '../../utils/ui/loginDebug';

/**
 * Sign-out handoff (2026-09-25, founder: "splash, then card"). Signing out of
 * the app used to tear the whole routes tree down in one frame (the session-
 * keyed remount + the jump to '/'), so every sign-out, expiry and revoke kick
 * hard-snapped to the login card. Now AuthContext.signOut covers the app with
 * the splash FIRST, the teardown and route change happen under it, and Login
 * releases it once it has painted — the splash fades away onto the card.
 *
 * Only when LEAVING the app: sign-outs on a pre-auth screen (Login's 2FA back
 * step, its failure paths, reset-password, accept-invite) keep their own
 * in-card motion. A failsafe drops the splash if nothing releases it, so it
 * can never strand anyone. UX only — revocation itself is unchanged.
 */
// Pre-auth screens (own motion) and public pages (no Login to hand off to).
const NO_COVER_PATHS = new Set(['/', '/reset-password', '/accept-invite', '/estimate', '/unsubscribe']);
const FAILSAFE_MS = 4000;

let active = false;
let failsafe = null;
const listeners = new Set();

/** True while the sign-out cover (the quiet veil) is up. */
export function isSignOutSplashActive() {
  return active;
}

function set(next) {
  if (next === active) return;
  active = next;
  ldLog('signoutsplash', { active });
  listeners.forEach((l) => l());
}

export function releaseSignOutSplash() {
  if (failsafe) { clearTimeout(failsafe); failsafe = null; }
  set(false);
}

/** True when a sign-out from here leaves the app (vs a pre-auth screen's own flow). */
export function isLeavingApp(pathname = typeof window !== 'undefined' ? window.location.pathname : '/') {
  return !NO_COVER_PATHS.has(pathname);
}

/**
 * Start the Login chunk download before the routes tree remounts, so its Suspense
 * fallback never fires under the veil. Goes through preAuthPages' preloadable Login (RD-LUI-016) so the
 * routes tree renders it synchronously; fire-and-forget, a failure is the real navigation's to retry.
 */
function warmLoginChunk() {
  try { void import('../../pages/preAuthPages').then((m) => m.Login.preload()).catch(() => {}); } catch (_e) { /* UI only */ }
}

/**
 * Raise the splash and resolve once it has faded fully in, so the caller's
 * teardown happens under an opaque cover. Resolves at once (no splash) on a
 * pre-auth/public screen or in a hidden tab. Never rejects.
 */
export function coverAppForSignOut() {
  if (!isLeavingApp()) return Promise.resolve();
  // Hidden tab (e.g. the idle timeout): nobody sees a splash, and browsers
  // throttle hidden-tab timers up to ~1 min — never delay revocation for it.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return Promise.resolve();
  if (failsafe) clearTimeout(failsafe);
  failsafe = setTimeout(releaseSignOutSplash, FAILSAFE_MS);
  set(true);
  warmLoginChunk();
  return new Promise((resolve) => { setTimeout(resolve, SPLASH_FADE_MS); });
}

/**
 * A sign-out we did NOT start (refresh-token expiry, revoke, another tab) that
 * should still get the splash handoff: in the app, tab visible, and no cover
 * already up (a user sign-out raised its own first). Everything else keeps the
 * old synchronous teardown byte-for-byte (2026-09-26 "rare snap").
 */
export function needsUnplannedSignOutCover() {
  if (active || !isLeavingApp()) return false;
  return !(typeof document !== 'undefined' && document.visibilityState === 'hidden');
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSignOutSplashActive() {
  return useSyncExternalStore(subscribe, () => active, () => false);
}

/** @internal test helper */
export function __resetSignOutSplashForTests() {
  if (failsafe) clearTimeout(failsafe);
  failsafe = null;
  active = false;
  listeners.clear();
}
